/**
 * Zombe TCG — Shopify Theme JavaScript
 * Handles: cart drawer, mobile menu, search, product gallery, quantity selectors,
 *           variant selection, FAQ accordion, add-to-cart AJAX, newsletter.
 */
(function () {
  'use strict';

  /* ─── Toast helper ─────────────────────────────────────────────────────── */
  function createToastContainer() {
    let el = document.querySelector('.toast-container');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast-container';
      document.body.appendChild(el);
    }
    return el;
  }

  function showToast(message, type = 'success', duration = 3000) {
    const container = createToastContainer();
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    container.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  /* ─── Formatters ─────────────────────────────────────────────────────────── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     CART — AJAX helpers
  ═══════════════════════════════════════════════════════════════════════ */
  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  }

  async function addToCart(id, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  async function changeCartItem(id, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    return res.json();
  }

  async function clearCart() {
    const res = await fetch('/cart/clear.js', { method: 'POST' });
    return res.json();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     CART DRAWER
  ═══════════════════════════════════════════════════════════════════════ */
  const CartDrawer = {
    overlay: null,
    drawer: null,
    itemsEl: null,
    subtotalEl: null,

    init() {
      this.overlay = document.getElementById('cart-drawer-overlay');
      this.drawer = document.getElementById('cart-drawer');
      this.itemsEl = document.getElementById('cart-drawer-items');
      this.subtotalEl = document.getElementById('cart-drawer-subtotal');

      if (!this.drawer) return;

      // Close buttons
      document.querySelectorAll('[data-cart-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      // Open buttons
      document.querySelectorAll('[data-cart-open]').forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      this.refreshCount();
    },

    open() {
      if (this.overlay) this.overlay.classList.add('open');
      if (this.drawer) this.drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
      this.renderItems();
    },

    close() {
      if (this.overlay) this.overlay.classList.remove('open');
      if (this.drawer) this.drawer.classList.remove('open');
      document.body.style.overflow = '';
    },

    async refreshCount() {
      try {
        const cart = await fetchCart();
        const count = cart.item_count || 0;
        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = count;
          el.classList.toggle('visible', count > 0);
        });
      } catch (e) { /* ignore */ }
    },

    async renderItems() {
      if (!this.itemsEl) return;
      this.itemsEl.innerHTML = '<p style="color:var(--muted);text-align:center;padding:2rem 0;">Loading…</p>';
      try {
        const cart = await fetchCart();
        if (!cart.items || cart.items.length === 0) {
          this.itemsEl.innerHTML = '<div class="cart-drawer-empty"><p>Your cart is empty.</p></div>';
          if (this.subtotalEl) this.subtotalEl.textContent = '$0.00';
          return;
        }
        this.itemsEl.innerHTML = cart.items.map(item => `
          <div class="cart-drawer-item">
            <div class="cart-drawer-item-img">
              ${item.image ? `<img src="${item.image}" alt="${item.title}" loading="eager">` : ''}
            </div>
            <div>
              <div class="cart-drawer-item-title">${item.product_title}</div>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-drawer-item-meta">${item.variant_title}</div>` : ''}
              <div class="cart-drawer-item-price">${formatMoney(item.final_line_price)} <span style="color:var(--muted);font-size:0.75rem;">× ${item.quantity}</span></div>
            </div>
          </div>
        `).join('');
        if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);
        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.classList.toggle('visible', cart.item_count > 0);
        });
      } catch (e) {
        this.itemsEl.innerHTML = '<div class="cart-drawer-empty"><p>Could not load cart.</p></div>';
      }
    },
  };

  /* ═══════════════════════════════════════════════════════════════════════
     MOBILE MENU
  ═══════════════════════════════════════════════════════════════════════ */
  const MobileMenu = {
    btn: null,
    menu: null,
    isOpen: false,

    init() {
      this.btn = document.getElementById('mobile-menu-btn');
      this.menu = document.getElementById('mobile-menu');
      if (!this.btn || !this.menu) return;
      this.btn.addEventListener('click', () => this.toggle());
    },

    toggle() {
      this.isOpen = !this.isOpen;
      this.menu.classList.toggle('open', this.isOpen);
      document.body.style.overflow = this.isOpen ? 'hidden' : '';
      const iconOpen  = this.btn.querySelector('[data-icon-open]');
      const iconClose = this.btn.querySelector('[data-icon-close]');
      if (iconOpen)  iconOpen.style.display  = this.isOpen ? 'none' : '';
      if (iconClose) iconClose.style.display = this.isOpen ? ''     : 'none';
    },
  };

  /* ═══════════════════════════════════════════════════════════════════════
     SEARCH BAR (toggle)
  ═══════════════════════════════════════════════════════════════════════ */
  const SearchBar = {
    btn: null,
    bar: null,
    input: null,

    init() {
      this.btn = document.getElementById('search-toggle-btn');
      this.bar = document.getElementById('search-bar');
      if (!this.btn || !this.bar) return;
      this.input = this.bar.querySelector('input');
      this.btn.addEventListener('click', () => this.toggle());
    },

    toggle() {
      const isOpen = this.bar.classList.toggle('open');
      if (isOpen && this.input) this.input.focus();
    },
  };

  /* ═══════════════════════════════════════════════════════════════════════
     PRODUCT DETAIL — Gallery, Variants, Qty, Add to Cart
  ═══════════════════════════════════════════════════════════════════════ */
  const ProductPage = {
    init() {
      this.initGallery();
      this.initVariants();
      this.initQtySelector();
      this.initAddToCart();
    },

    initGallery() {
      const mainImg = document.getElementById('product-main-image');
      if (!mainImg) return;
      document.querySelectorAll('.product-gallery-thumb').forEach(thumb => {
        thumb.addEventListener('click', () => {
          mainImg.src = thumb.dataset.src;
          document.querySelectorAll('.product-gallery-thumb').forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        });
      });
    },

    initVariants() {
      const select = document.getElementById('variant-select');
      if (!select) return;
      select.addEventListener('change', () => {
        const selected = select.options[select.selectedIndex];
        // Update price
        const priceEl = document.getElementById('product-price');
        if (priceEl && selected.dataset.price) {
          priceEl.textContent = formatMoney(parseInt(selected.dataset.price, 10));
        }
        // Update compare price
        const compareEl = document.getElementById('product-compare-price');
        if (compareEl) {
          const cp = parseInt(selected.dataset.comparePrice || '0', 10);
          compareEl.textContent = cp > 0 ? formatMoney(cp) : '';
        }
        // Update availability
        const atcBtn = document.getElementById('product-atc-btn');
        const available = selected.dataset.available !== 'false';
        if (atcBtn) {
          atcBtn.disabled = !available;
          atcBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
        }
      });
    },

    initQtySelector() {
      const input = document.getElementById('qty-input');
      if (!input) return;
      document.getElementById('qty-minus')?.addEventListener('click', () => {
        const v = parseInt(input.value, 10);
        if (v > 1) input.value = v - 1;
      });
      document.getElementById('qty-plus')?.addEventListener('click', () => {
        input.value = parseInt(input.value, 10) + 1;
      });
    },

    initAddToCart() {
      const form = document.getElementById('product-atc-form');
      if (!form) return;
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const variantId = document.getElementById('variant-select')?.value
          || form.querySelector('[name="id"]')?.value;
        const qty = parseInt(document.getElementById('qty-input')?.value || '1', 10);
        const btn = document.getElementById('product-atc-btn');
        if (!variantId) return;

        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          await addToCart(variantId, qty);
          showToast('Added to cart!', 'success');
          CartDrawer.refreshCount();
          CartDrawer.open();
        } catch (err) {
          showToast(err.message || 'Could not add to cart', 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = 'Add to Cart';
        }
      });
    },
  };

  /* ═══════════════════════════════════════════════════════════════════════
     PRODUCT CARDS — Quick add to cart
  ═══════════════════════════════════════════════════════════════════════ */
  function initProductCardButtons() {
    document.querySelectorAll('.product-card-atc').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          await addToCart(variantId, 1);
          showToast('Added to cart!', 'success');
          CartDrawer.refreshCount();
        } catch (err) {
          showToast(err.message || 'Could not add to cart', 'error');
        } finally {
          btn.disabled = false;
          btn.textContent = original;
        }
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     CART PAGE
  ═══════════════════════════════════════════════════════════════════════ */
  function initCartPage() {
    // Quantity changes
    document.querySelectorAll('.cart-page-qty').forEach(input => {
      input.addEventListener('change', async () => {
        const key = input.dataset.key;
        const qty = parseInt(input.value, 10);
        if (!key) return;
        await changeCartItem(key, qty);
        window.location.reload();
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     FAQ ACCORDION
  ═══════════════════════════════════════════════════════════════════════ */
  function initFaqAccordion() {
    document.querySelectorAll('.faq-question').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.faq-item');
        const isOpen = item.classList.contains('open');
        // Close all
        document.querySelectorAll('.faq-item').forEach(i => i.classList.remove('open'));
        // Toggle current
        if (!isOpen) item.classList.add('open');
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     NEWSLETTER (Shopify Customer endpoint)
  ═══════════════════════════════════════════════════════════════════════ */
  function initNewsletter() {
    document.querySelectorAll('.newsletter-form').forEach(form => {
      form.addEventListener('submit', (e) => {
        // Let Shopify handle the native form submit to /contact#contact_form
        // or swap for a fetch if you have a custom endpoint
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     HERO SEARCH
  ═══════════════════════════════════════════════════════════════════════ */
  function initHeroSearch() {
    const form = document.getElementById('hero-search-form');
    if (!form) return;
    form.addEventListener('submit', (e) => {
      const input = form.querySelector('input[type="search"]');
      if (!input || !input.value.trim()) e.preventDefault();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     INIT
  ═══════════════════════════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', () => {
    CartDrawer.init();
    MobileMenu.init();
    SearchBar.init();
    ProductPage.init();
    initProductCardButtons();
    initCartPage();
    initFaqAccordion();
    initNewsletter();
    initHeroSearch();
  });

})();
